import supabase from '../supabase/supabase';

const PRODUCT_SELECT = `
  id,
  name,
  slug,
  description,
  price,
  status,
  availability_status,
  featured,
  seo_title,
  seo_description,
  created_at,
  category:zevi_categories_20260925(id,name,slug),
  media:zevi_product_media_20260925(
    id,
    media_url,
    media_type,
    alt_text,
    sort_order,
    is_primary,
    variant_id
  ),
  variants:zevi_product_variants_20260925(
    id,
    name,
    sku,
    color,
    color_hex,
    price_override,
    attributes,
    active
  ),
  inventory:zevi_inventory_20260925(
    id,
    variant_id,
    quantity_on_hand,
    quantity_reserved,
    quantity_committed,
    low_stock_threshold
  )
`;

function sortMedia(media) {
  return [...media].sort((first, second) => {
    if (first.is_primary !== second.is_primary) {
      return first.is_primary ? -1 : 1;
    }

    return Number(first.sort_order || 0) - Number(second.sort_order || 0);
  });
}

function sortColorVariants(variants) {
  return variants
    .map((variant, index) => ({
      variant,
      index,
      order: Number.isFinite(Number(variant.attributes?.color_order))
        ? Number(variant.attributes.color_order)
        : index
    }))
    .sort((first, second) => (
      first.order - second.order || first.index - second.index
    ))
    .map(({ variant }) => variant);
}

function normalizeProduct(product) {
  const allMedia = product.media || [];
  const generalMedia = sortMedia(
    allMedia.filter((media) => !media.variant_id)
  );
  const inventory = product.inventory || [];

  const availableStock = inventory.reduce((total, item) => (
    total + Math.max(
      0,
      Number(item.quantity_on_hand || 0)
        - Number(item.quantity_reserved || 0)
        - Number(item.quantity_committed || 0)
    )
  ), 0);

  const colorVariants = sortColorVariants(product.variants || [])
    .filter((variant) => (
      variant.active && (variant.color || variant.attributes?.color)
    ))
    .map((variant) => {
      const variantMedia = sortMedia(
        allMedia.filter((media) => media.variant_id === variant.id)
      );
      const fallbackImage = generalMedia[0]?.media_url || '';

      return {
        id: variant.id,
        name: variant.color || variant.attributes?.color || variant.name,
        sku: variant.sku,
        color: variant.color || variant.attributes?.color || '',
        colorHex:
          variant.color_hex
          || variant.attributes?.color_hex
          || '#c99a45',
        priceOverride: variant.price_override,
        image: variantMedia[0]?.media_url || fallbackImage,
        media: variantMedia,
        inventory: inventory.find((item) => item.variant_id === variant.id)
      };
    });

  const firstVariantImage = colorVariants.find(
    (variant) => variant.image
  )?.image;

  return {
    ...product,
    media: generalMedia,
    image: generalMedia[0]?.media_url || firstVariantImage || '',
    imageAlt:
      generalMedia[0]?.alt_text
      || `${product.name} saree`,
    variants: (product.variants || []).filter((variant) => variant.active),
    colorVariants,
    availableStock,
    inStock: availableStock > 0
  };
}

export async function getProducts({
  search = '',
  category = '',
  availability = '',
  sort = 'featured',
  limit = 12,
  offset = 0
} = {}) {
  let categoryId = '';

  if (category) {
    const { data: categoryRow, error: categoryError } = await supabase
      .from('zevi_categories_20260925')
      .select('id')
      .eq('slug', category)
      .eq('active', true)
      .maybeSingle();

    if (categoryError) {
      throw new Error('The selected category could not be loaded.');
    }

    if (!categoryRow) {
      return { products: [], count: 0 };
    }

    categoryId = categoryRow.id;
  }

  let query = supabase
    .from('zevi_products_20260925')
    .select(PRODUCT_SELECT, { count: 'exact' })
    .eq('status', 'active')
    .is('archived_at', null)
    .range(offset, offset + limit - 1);

  if (search.trim()) {
    query = query.ilike('name', `%${search.trim()}%`);
  }

  if (categoryId) {
    query = query.eq('category_id', categoryId);
  }

  if (availability) {
    query = query.eq('availability_status', availability);
  }

  if (sort === 'newest') {
    query = query.order('created_at', { ascending: false });
  } else if (sort === 'price_low') {
    query = query.order('price', { ascending: true });
  } else if (sort === 'price_high') {
    query = query.order('price', { ascending: false });
  } else {
    query = query
      .order('featured', { ascending: false })
      .order('created_at', { ascending: true });
  }

  const { data, error, count } = await query;

  if (error) {
    throw new Error('The ZEVI collection is temporarily unavailable.');
  }

  return {
    products: (data || []).map(normalizeProduct),
    count: count || 0
  };
}

export async function getProductBySlug(slug) {
  const { data, error } = await supabase
    .from('zevi_products_20260925')
    .select(PRODUCT_SELECT)
    .eq('slug', slug)
    .eq('status', 'active')
    .is('archived_at', null)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }

    throw new Error('This product could not be loaded.');
  }

  return normalizeProduct(data);
}

export async function getCategories() {
  const { data, error } = await supabase
    .from('zevi_categories_20260925')
    .select('id,name,slug')
    .eq('active', true)
    .order('sort_order');

  if (error) {
    throw new Error('Categories could not be loaded.');
  }

  return data || [];
}